import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Plus, Eye, Edit2, Clock, Users, Calendar } from 'lucide-react';

const CompanyProjects = () => {
  const { profile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/company/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.status === filter);

  const statusColors = {
    pending: 'badge-warning',
    active: 'badge-success',
    paused: 'badge-neutral',
    filled: 'badge-primary',
    closed: 'badge-error',
  };

  const functionColors = {
    product: 'badge-primary',
    design: 'badge-accent',
    engineering: 'bg-emerald-100 text-emerald-700',
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-projects-page">
      <Header />

      <div className="container-custom py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="heading-2 mb-2">My Projects</h1>
            <p className="text-slate-600">Manage your project listings</p>
          </div>

          <div className="flex gap-4 mt-4 md:mt-0">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="filled">Filled</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            {profile?.status === 'approved' && (
              <Link to="/company/projects/new">
                <Button className="btn-accent" data-testid="new-project-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Project
                </Button>
              </Link>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="p-6 animate-pulse">
                <div className="h-5 bg-slate-200 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
                <div className="flex gap-2">
                  <div className="h-6 bg-slate-200 rounded w-16"></div>
                  <div className="h-6 bg-slate-200 rounded w-20"></div>
                </div>
              </Card>
            ))}
          </div>
        ) : filteredProjects.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="p-6" data-testid={`project-card-${project.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="heading-4 mb-1">{project.title}</h3>
                    <div className="flex items-center gap-2">
                      <span className={`${functionColors[project.function]} px-2 py-0.5 rounded-full text-xs`}>
                        {project.function}
                      </span>
                      <span className={`${statusColors[project.status]} text-xs`}>
                        {project.status}
                      </span>
                      {project.is_featured && (
                        <span className="badge-accent text-xs">Featured</span>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                  {project.description}
                </p>

                <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-4">
                  <span className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {project.hours_per_week}h/week
                  </span>
                  <span className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {project.duration}
                  </span>
                  <span className="flex items-center">
                    <Users className="h-4 w-4 mr-1" />
                    {project.applicant_count || 0} applicants
                  </span>
                </div>

                <div className="flex gap-2 pt-4 border-t border-slate-100">
                  <Link to={`/company/projects/${project.id}`} className="flex-1">
                    <Button variant="outline" className="w-full" data-testid={`view-project-${project.id}`}>
                      <Eye className="h-4 w-4 mr-2" />
                      View Applicants
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Plus className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="heading-4 mb-2">No projects yet</h3>
            <p className="text-slate-500 mb-6">
              {filter !== 'all' 
                ? `No projects with "${filter}" status`
                : 'Post your first project to start finding talent'}
            </p>
            {profile?.status === 'approved' && (
              <Link to="/company/projects/new">
                <Button className="btn-primary" data-testid="empty-new-project-btn">
                  Post Your First Project
                </Button>
              </Link>
            )}
          </Card>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CompanyProjects;
